import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';

import { State } from '®app/ngrx/state';
import { HierarchyNode } from '®shared/hierarchy';

import {
  NodeSearchClearedAction,
  NodeSearchInitiatedAction,
} from './node-search.actions';
import { NodeSearchState, selectNodeSearchState } from './nodes-editor.state';

export const DEBOUNCE_SEARCH_CHANGES_MS = 750;
export const SEARCH_TERM_MAX_LENGTH_CHARS = 100;

export const MAX_NODES_LIMIT = 5;

@Component({
  selector: 'rl-node-search-field',
  templateUrl: './node-search-field.component.html',
})
export class NodeSearchFieldComponent implements OnChanges, OnDestroy, OnInit {
  MAX_NODES_LIMIT = MAX_NODES_LIMIT;

  @Input() disabled: boolean;
  @Output() nodeSelected = new EventEmitter<HierarchyNode>();

  nodeCtrl: FormControl;

  private searchStateSubscription: Subscription;
  searchState: NodeSearchState;

  constructor(
    private cd: ChangeDetectorRef,
    private formBuilder: FormBuilder,
    private store: Store<State>,
  ) {}

  createForm() {
    this.nodeCtrl = this.formBuilder.control('', [
      Validators.pattern(/\S/),
      Validators.maxLength(SEARCH_TERM_MAX_LENGTH_CHARS),
    ]);
    if (this.disabled) {
      this.nodeCtrl.disable();
    }
  }

  ngOnDestroy() {
    this.searchStateSubscription.unsubscribe();
  }

  ngOnInit() {
    this.searchStateSubscription = this.store
      .select(selectNodeSearchState)
      .subscribe(searchState => {
        this.searchState = searchState;
        this.cd.markForCheck();
      });

    this.createForm();

    this.nodeCtrl.valueChanges
      .pipe(
        debounceTime(DEBOUNCE_SEARCH_CHANGES_MS),
        distinctUntilChanged(),
        map(searchTerm => (searchTerm ? searchTerm.trim() : null)),
      )
      .forEach(searchTerm => {
        if (this.disabled) {
          return;
        }

        const action = searchTerm
          ? new NodeSearchInitiatedAction({
              searchTerm,
              limit: MAX_NODES_LIMIT,
            })
          : new NodeSearchClearedAction();

        this.store.dispatch(action);
      });
  }

  ngOnChanges(changes?: SimpleChanges) {
    if (
      changes &&
      changes.disabled &&
      this.nodeCtrl &&
      this.nodeCtrl.disabled !== this.disabled
    ) {
      if (this.disabled) {
        this.nodeCtrl.disable();
      } else {
        this.nodeCtrl.enable();
      }
    }
  }

  /**
   * When an autocomplete node is selected, clear the input field and emit the node to `nodeSelected`
   * @param {MatAutocompleteSelectedEvent} $event The event containing the selected node
   */
  selectNode($event: MatAutocompleteSelectedEvent): void {
    if (!this.disabled) {
      this.nodeSelected.emit($event.option.value);
    }

    // never display the selected object - after selection, we clear the input
    this.nodeCtrl.reset();
  }
}
